import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService from '../../services/unitService';
import './ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const UnitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [unit, setUnit] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUnit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUnit = async () => {
    setLoading(true);
    setError(null);
    try {
      const unit = await unitService.getUnitById(parseInt(id || '0'));
      if (unit) {
        setUnit(unit);
      }
      setLoading(false);
    } catch (err) {
      setError(t('unit.failedToLoadUnit'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!unit) return;
    navigate(`/products/units/${unit.unitId}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!unit) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{t('unit.failedToLoadUnit')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/products/units')}
            title={t('unit.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('unit.title')}
          </button>
        </nav>

        <div className="detail-header">
          <h1 className="detail-title">{unit.unitName}</h1>
          <p className="detail-subtitle">{t('common.view')}</p>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('unit.title')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('unit.unitName')}</label>
                    <p className="detail-value"><strong>{unit.unitName}</strong></p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('unit.abbreviation')}</label>
                    <p className="detail-value detail-code">{unit.unitShortName}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('unit.unitCode')}</label>
                    <p className="detail-value detail-code">{unit.unitCode}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge" style={{ backgroundColor: unit.isActive ? '#28a745' : '#6c757d' }}>
                        {unit.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('unit.description')}</label>
                    <p className="detail-value detail-description">
                      {unit.description || <em className="detail-empty">{t('common.noDescription')}</em>}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.information')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {unit.createdDate ? new Date(unit.createdDate).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US') : '-'}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime')}</label>
                    <p className="detail-value">
                      {unit.createdDate ? new Date(unit.createdDate).toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : 'en-US') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/products/units/${unit.unitId}/edit`)}
                  title={t('common.edit')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/products/units')}
                  title={t('unit.title')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('unit.title')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitDetailPage;
